import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

import { useSeo } from "@/lib/seo";
import { waLink } from "@/data/site";
import { getArticle } from "@/data/articles";
import { getLocation } from "@/data/locations";
import { PageHeader } from "@/components/site/PageHeader";
import { SSButton } from "@/components/site/SSButton";
import { StatusBadge } from "@/components/site/StatusBadge";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "geography", q: "What are you considering?", sub: "Where in Uttarakhand?",
    options: ["Dehradun", "Mussoorie", "Rishikesh", "Kumaon", "Elsewhere in Uttarakhand", "Still exploring"] },
  { key: "property_type", q: "What exactly are you considering?", sub: "Choose the closest fit — this changes the checks that matter.",
    options: ["Residential plot", "Agricultural / horticultural land", "Independent home", "Apartment", "Commercial property", "Hospitality / project land", "Not sure"] },
  { key: "documents", q: "How much documentation have you seen?", sub: "Do not worry if you are at an early stage.",
    options: ["Full document set", "Some documents", "Only the listing / brochure", "Nothing yet"] },
  { key: "commitment", q: "Have you paid or committed any money?", sub: "This helps us understand urgency.",
    options: ["No", "Token paid", "Agreement / advance paid", "Prefer not to say"] },
  { key: "budget", q: "Approximate budget band?", sub: "Broad ranges only — nothing invasive.",
    options: ["Under ₹1 Cr", "₹1–3 Cr", "₹3–7 Cr", "₹7–15 Cr", "₹15 Cr +", "Prefer not to say"] },
  { key: "intended_use", q: "Intended use?", sub: "What is it primarily for?",
    options: ["Primary residence", "Second home", "Investment", "Development", "Retirement", "Other"] },
  { key: "timeline", q: "Timeline?", sub: "How soon are you looking to act?",
    options: ["Exploring", "0–3 months", "3–6 months", "6–12 months", "Longer term"] },
  { key: "concern", q: "Main concern?", sub: "What worries you most right now?",
    options: ["Legal / title", "Location", "Price / value", "Construction", "Land rules", "Resale", "Rental potential", "Due diligence", "Not sure yet"] },
];

const GEO_LOCATION = {
  Dehradun: "dehradun", Mussoorie: "mussoorie", Rishikesh: "rishikesh", Kumaon: "nainital",
};
const CONCERN_ARTICLE = {
  "Legal / title": "can-you-buy-property-in-dehradun-uttarakhand-land-law-guide",
  Location: "the-doon-valley-as-a-property-market",
  "Price / value": "understanding-circle-rates-in-uttarakhand",
  Construction: "building-on-a-slope-what-changes-in-the-hills",
  "Land rules": "can-you-buy-property-in-dehradun-uttarakhand-land-law-guide",
  Resale: "how-to-value-a-view",
  "Rental potential": "second-homes-in-uttarakhand-the-operational-reality",
  "Due diligence": "a-practical-property-due-diligence-checklist",
  "Not sure yet": "buying-property-in-uttarakhand-a-structured-approach",
};

export default function Guided() {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  useSeo({
    title: "Buyer Assistance | Guided Property Intelligence",
    description:
      "A private buyer-assistance journey for Uttarakhand property decisions: property type, documents, commitment stage, intended use, legal concerns and due-diligence reading.",
    path: "/guided",
  });

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  const current = STEPS[step];
  const progress = showResult ? 100 : Math.round((step / STEPS.length) * 100);

  const choose = async (value) => {
    const next = { ...answers, [current.key]: value };
    setAnswers(next);
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      setShowResult(true);
      try { await axios.post(`${API}/guided/submit`, next); } catch (e) { /* non-blocking */ }
    }
  };

  const back = () => {
    if (showResult) { setShowResult(false); return; }
    if (step > 0) setStep((s) => s - 1);
  };

  const recommendations = () => {
    const locSlug = GEO_LOCATION[answers.geography];
    const loc = locSlug ? getLocation(locSlug) : null;
    const artSlugs = [
      CONCERN_ARTICLE[answers.concern] || "buying-property-in-uttarakhand-a-structured-approach",
      "can-you-buy-property-in-dehradun-uttarakhand-land-law-guide",
      "a-practical-property-due-diligence-checklist",
    ];
    const articles = [...new Set(artSlugs)].map(getArticle).filter(Boolean).slice(0, 3);
    return { loc, articles };
  };

  const summary = Object.entries(answers)
    .map(([k, v]) => `${STEPS.find((s) => s.key === k)?.q.replace("?", "")}: ${v}`)
    .join("; ");
  const waMessage = `Hi Suhail, I used the Buyer Assistance tool. ${summary}. I'd like to discuss this property before I commit.`;

  return (
    <>
      <PageHeader
        eyebrow="Buyer Assistance"
        marker="SS·GDE"
        title={showResult ? "Your buyer-intelligence starting point" : "Understand the property before you commit"}
        intro={
          showResult
            ? "This does not analyse a specific property. It assembles the intelligence most relevant to what you told us — a place to begin reading, and the questions worth investigating."
            : "Tell us what you are considering, what documents you have seen and where you are in the decision. We will orient you toward the checks and intelligence that matter — without pretending an automated questionnaire can legally clear a property."
        }
        breadcrumbs={[{ label: "Buyer Assistance" }]}
      />

      <section className="ss-container pb-28" data-testid="guided">
        {/* progress */}
        <div className="flex items-center gap-4 mb-10">
          <div className="h-px flex-1 bg-[#E2DDD5] relative overflow-hidden">
            <motion.div className="absolute inset-y-0 left-0 bg-[#2D3B32]" animate={{ width: `${progress}%` }} transition={{ ease: [0.25, 1, 0.5, 1], duration: 0.5 }} />
          </div>
          <span className="font-mono-ss text-[0.66rem] tracking-widest text-[#8A9098]">
            {showResult ? "COMPLETE" : `STEP ${step + 1} / ${STEPS.length}`}
          </span>
        </div>

        {(step > 0 || showResult) && (
          <button onClick={back} className="mb-8 inline-flex items-center gap-2 text-[0.74rem] uppercase tracking-[0.1em] text-[#52575D] hover:text-[#1A1C1E]" data-testid="guided-back">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back
          </button>
        )}

        <AnimatePresence mode="wait">
          {!showResult ? (
            <motion.div
              key={current.key}
              initial={{ opacity: 0, y: reduce ? 0 : 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: reduce ? 0 : -20 }}
              transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
            >
              <p className="ss-overline text-[#8C6A53] mb-3">{current.sub}</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1A1C1E] leading-[1.02] max-w-3xl">{current.q}</h2>
              <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {current.options.map((opt, i) => (
                  <motion.button
                    key={opt}
                    initial={{ opacity: 0, y: reduce ? 0 : 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 + i * 0.04 }}
                    onClick={() => choose(opt)}
                    data-testid={`guided-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={cn(
                      "group text-left border p-6 transition-all duration-300",
                      answers[current.key] === opt
                        ? "border-[#1A1C1E] bg-[#1A1C1E] text-[#F7F5F0]"
                        : "border-[#C8C2B7] bg-[#F2EFE9] hover:border-[#1A1C1E] hover:bg-[#EFECE6]"
                    )}
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span className="font-display text-xl">{opt}</span>
                      <ArrowUpRight className="h-4 w-4 opacity-40 transition-all group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          ) : (
            <Result key="result" reduce={reduce} recommendations={recommendations()} answers={answers} waMessage={waMessage} navigate={navigate} />
          )}
        </AnimatePresence>
      </section>
    </>
  );
}

function Result({ recommendations, answers, waMessage, navigate }) {
  const { loc, articles } = recommendations;
  const advisoryHref = `/advisory?context=${encodeURIComponent(
    `Guided intelligence summary — ${Object.entries(answers).map(([k, v]) => `${k}: ${v}`).join(", ")}`
  )}${loc ? `&location=${encodeURIComponent(loc.name)}` : ""}`;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="guided-result">
      {/* chosen summary */}
      <div className="flex flex-wrap gap-2 mb-12">
        {Object.entries(answers).map(([k, v]) => (
          <span key={k} className="border border-[#C8C2B7] bg-[#F2EFE9] px-3 py-1.5 font-mono-ss text-[0.66rem] uppercase tracking-wider text-[#52575D]">{v}</span>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
        {/* Location guide */}
        <div className="lg:col-span-1">
          <p className="ss-overline text-[#8C6A53] mb-4">Start with the place</p>
          {loc ? (
            <Link to={`/locations/${loc.slug}`} className="group block border border-[#E2DDD5] bg-[#F2EFE9] p-7 h-full" data-testid="result-location">
              <p className="font-mono-ss text-[0.64rem] tracking-wider text-[#8A9098] mb-2">{loc.coords}</p>
              <h3 className="font-display text-3xl font-light text-[#1A1C1E]">{loc.name}</h3>
              <p className="mt-2 text-[0.92rem] font-light text-[#52575D]">{loc.tagline}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[0.74rem] uppercase tracking-[0.1em]"><span className="ss-link">Open intelligence</span><ArrowUpRight className="h-4 w-4" strokeWidth={1.5} /></span>
            </Link>
          ) : (
            <Link to="/locations" className="group block border border-[#E2DDD5] bg-[#F2EFE9] p-7 h-full" data-testid="result-location">
              <h3 className="font-display text-3xl font-light text-[#1A1C1E]">Explore locations</h3>
              <p className="mt-2 text-[0.92rem] font-light text-[#52575D]">Compare places across Garhwal and Kumaon to narrow your focus.</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[0.74rem] uppercase tracking-[0.1em]"><span className="ss-link">All locations</span><ArrowUpRight className="h-4 w-4" strokeWidth={1.5} /></span>
            </Link>
          )}
        </div>

        {/* Articles */}
        <div className="lg:col-span-2">
          <p className="ss-overline text-[#8C6A53] mb-4">Read this next</p>
          <div className="space-y-3">
            {articles.map((a) => (
              <Link key={a.slug} to={`/journal/${a.slug}`} className="group flex items-center justify-between gap-4 border border-[#E2DDD5] bg-[#F2EFE9] p-5 hover:border-[#1A1C1E] transition-colors" data-testid={`result-article-${a.slug}`}>
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="ss-overline text-[#8C6A53]">{a.category}</span>
                    <StatusBadge status={a.status} />
                  </div>
                  <h3 className="font-display text-xl font-normal text-[#1A1C1E]">{a.title}</h3>
                </div>
                <ArrowUpRight className="h-5 w-5 shrink-0 text-[#8A9098] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#1A1C1E]" strokeWidth={1.3} />
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Due diligence + CTA */}
      <div className="mt-10 border border-[#2D3B32]/25 bg-[#EFECE6] p-8 lg:p-12 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
        <div>
          <p className="ss-overline text-[#8C6A53] mb-3">Then investigate</p>
          <h3 className="font-display text-2xl sm:text-3xl font-light text-[#1A1C1E]">Run the due-diligence checklist before you commit</h3>
          <p className="mt-3 text-[0.98rem] font-light text-[#52575D] max-w-lg">
            Our practical checklist walks through title, records, access, planning and physical verification — the checks a property should survive before purchase.
          </p>
          <Link to="/journal/a-practical-property-due-diligence-checklist" className="group mt-5 inline-flex items-center gap-1.5 text-[0.76rem] uppercase tracking-[0.1em] text-[#1A1C1E]"><span className="ss-link">Open the checklist</span><ArrowUpRight className="h-4 w-4" strokeWidth={1.5} /></Link>
        </div>
        <div className="flex flex-col gap-4">
          <SSButton onClick={() => navigate("/buyer-assistance/documents")} variant="primary" data-testid="result-documents">Check what documents you have</SSButton>
          <SSButton onClick={() => navigate(advisoryHref)} variant="secondary" data-testid="result-advisory">Discuss this with Selling Sunsets</SSButton>
          <SSButton href={waLink(waMessage)} variant="secondary" arrow={false} data-testid="result-whatsapp">Continue on WhatsApp</SSButton>
          <p className="text-[0.72rem] font-light text-[#8A9098]">Your answers will accompany the enquiry so we can respond in context.</p>
        </div>
      </div>
    </motion.div>
  );
}
